<template>
  <component
    :is="iconComponent"
    :class="iconClasses"
    :style="iconStyles"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  // Navigation icons
  UsersIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
  CalendarIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  
  // Action icons
  PlusIcon,
  PlusCircleIcon,
  MinusIcon,
  PencilIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ArrowPathRoundedSquareIcon,
  ArrowRightOnRectangleIcon,
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  
  // Status icons
  CheckIcon,
  CheckCircleIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  
  // UI icons
  Bars3Icon,
  EllipsisHorizontalIcon,
  EllipsisVerticalIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CloudArrowUpIcon,
  SparklesIcon,
  
  // Social icons
  // Note: Heroicons doesn't have LinkedIn/Twitter, keeping custom for now
  
  // Loading
  // Note: Using custom loader for now
  
  // Help
  QuestionMarkCircleIcon,
  ClipboardDocumentListIcon,
  BookmarkIcon,
  BookOpenIcon,
  
  // Communication icons
  ChatBubbleBottomCenterTextIcon,
  LinkIcon,
  MicrophoneIcon,
  
  // Visibility icons
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/vue/24/outline'

type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

interface Props {
  name: string
  size?: IconSize
  fill?: string
  stroke?: string
  strokeWidth?: string | number
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5
})

const iconClasses = computed(() => [
  'nvx-icon',
  `nvx-icon--${props.size}`
])

const iconStyles = computed(() => ({
  width: 'sizeValue.value',
  height: sizeValue.value,
  fill: props.fill,
  stroke: props.stroke,
  strokeWidth: props.strokeWidth
}))

const sizeValue = computed(() => {
  const sizeMap = {
    xs: '12px',
    sm: '16px',
    md: '20px',
    lg: '24px',
    xl: '32px'
  }
  return sizeMap[props.size]
})

const iconComponent = computed(() => {
  const iconMap: Record<string, any> = {
    // Navigation
    users: UsersIcon,
    'bar-chart': ChartBarIcon,
    'presentation-chart-line': PresentationChartLineIcon,
    calendar: CalendarIcon,
    'calendar-days': CalendarDaysIcon,
    'file-text': DocumentTextIcon,
    settings: Cog6ToothIcon,
    
    // Actions
    plus: PlusIcon,
    'plus-circle': PlusCircleIcon,
    minus: MinusIcon,
    edit: PencilIcon,
    'pencil-square': PencilSquareIcon,
    delete: TrashIcon,
    search: MagnifyingGlassIcon,
    refresh: ArrowPathRoundedSquareIcon,
    'arrow-path-rounded-square': ArrowPathRoundedSquareIcon,
    logout: ArrowRightOnRectangleIcon,
    'external-link': ArrowTopRightOnSquareIcon,
    'arrow-top-right-on-square': ArrowTopRightOnSquareIcon,
    'chevron-left': ChevronLeftIcon,
    'chevron-right': ChevronRightIcon,
    'chevron-up': ChevronUpIcon,
    'chevron-down': ChevronDownIcon,
    'arrow-trending-up': ArrowTrendingUpIcon,
    'arrow-trending-down': ArrowTrendingDownIcon,
    'clipboard-document-list': ClipboardDocumentListIcon,
    
    // Status
    check: CheckIcon,
    'check-circle': CheckCircleIcon,
    x: XMarkIcon,
    'x-mark': XMarkIcon,
    alert: ExclamationTriangleIcon,
    info: InformationCircleIcon,
    'information-circle': InformationCircleIcon,
    
    // UI
    menu: Bars3Icon,
    'more-horizontal': EllipsisHorizontalIcon,
    'more-vertical': EllipsisVerticalIcon,
    'dots-vertical': EllipsisVerticalIcon,
    filter: FunnelIcon,
    download: ArrowDownTrayIcon,
    upload: ArrowUpTrayIcon,
    'cloud-arrow-up': CloudArrowUpIcon,
    sparkles: SparklesIcon,
    
    // Help
    help: QuestionMarkCircleIcon,
    bookmark: BookmarkIcon,
    'book-open': BookOpenIcon,
    
    // Communication
    'chat-bubble-bottom-center-text': ChatBubbleBottomCenterTextIcon,
    'message-circle': ChatBubbleBottomCenterTextIcon, // Alias for backward compatibility
    link: LinkIcon,
    microphone: MicrophoneIcon,
    
    // Visibility
    eye: EyeIcon,
    'eye-slash': EyeSlashIcon
  }
  
  return iconMap[props.name] || QuestionMarkCircleIcon
})
</script>

<style lang="scss" scoped>
.nvx-icon {
  display: inline-block;
  flex-shrink: 0;
  vertical-align: middle;

  &--xs {
    width: 12px;
    height: 12px;
  }

  &--sm {
    width: 16px;
    height: 16px;
  }

  &--md {
    width: 20px;
    height: 20px;
  }

  &--lg {
    width: 24px;
    height: 24px;
  }

  &--xl {
    width: 32px;
    height: 32px;
  }
}
</style>
